import { EmailProviderCode } from '@today/types'
import { Badge, Button, Input, Label } from '@today/ui-primitives'
import { type ReactElement, useId } from 'react'

/** The editable sender settings; credentials are write-only and start empty. */
export interface EmailSenderDraft {
	provider: EmailProviderCode
	fromName: string
	fromAddress: string
	replyTo: string
	/** The tenant's unsubscribe mailbox (TO-138); empty = none. */
	unsubscribeMailbox: string
	accessKeyId: string
	secretAccessKey: string
	region: string
}

/**
 * Props for {@link EmailSenderForm}.
 *
 * @property value - The draft (controlled).
 * @property hasCredentials - Whether credentials are stored; shows a badge, never the values.
 * @property onChange - Reports one changed field.
 * @property onSave - Saves the draft.
 * @property onRemove - Removes the settings (back to the platform sender).
 * @property isDisabled - Read-only access: every input and button disabled.
 * @property isBusy - A save or remove is in flight.
 * @property error - A form-level message.
 */
export interface EmailSenderFormProps {
	value: EmailSenderDraft
	hasCredentials: boolean
	onChange: (field: keyof EmailSenderDraft, value: string) => void
	onSave: () => void
	onRemove: () => void
	isDisabled?: boolean
	isBusy?: boolean
	error?: string | null
}

const PROVIDER_LABELS: Record<EmailProviderCode, string> = {
	[EmailProviderCode.SES]: 'Amazon SES',
}

function TextField(props: {
	label: string
	value: string
	onChange: (value: string) => void
	disabled?: boolean
	type?: 'text' | 'email' | 'password'
}): ReactElement {
	const id = useId()
	return (
		<div className="flex flex-col gap-1.5">
			<Label htmlFor={id}>{props.label}</Label>
			<Input
				id={id}
				type={props.type ?? 'text'}
				value={props.value}
				disabled={props.disabled}
				autoComplete="off"
				onChange={(event) => props.onChange(event.target.value)}
			/>
		</div>
	)
}

function ProviderSelect(props: { value: EmailProviderCode; disabled: boolean; onChange: (value: string) => void }) {
	const id = useId()
	return (
		<div className="flex flex-col gap-1.5">
			<Label htmlFor={id}>Provider</Label>
			<select
				id={id}
				className="h-10 rounded-ds-md border border-ds-border bg-ds-surface-muted px-3 text-ds-base"
				value={props.value}
				disabled={props.disabled}
				onChange={(event) => props.onChange(event.target.value)}
			>
				{Object.values(EmailProviderCode).map((code) => (
					<option key={code} value={code}>
						{PROVIDER_LABELS[code]}
					</option>
				))}
			</select>
		</div>
	)
}

/** The three address fields: from, reply-to and the unsubscribe mailbox (TO-138). */
function AddressFields(props: {
	value: EmailSenderDraft
	disabled: boolean
	onChange: EmailSenderFormProps['onChange']
}) {
	const { value, disabled, onChange } = props
	return (
		<>
			<TextField
				label="From address"
				type="email"
				value={value.fromAddress}
				disabled={disabled}
				onChange={(v) => onChange('fromAddress', v)}
			/>
			<TextField
				label="Reply-to"
				type="email"
				value={value.replyTo}
				disabled={disabled}
				onChange={(v) => onChange('replyTo', v)}
			/>
			<TextField
				label="Unsubscribe mailbox"
				type="email"
				value={value.unsubscribeMailbox}
				disabled={disabled}
				onChange={(v) => onChange('unsubscribeMailbox', v)}
			/>
		</>
	)
}

/** Own SES credentials: write-only, never pre-filled; a badge says when some are stored. */
function CredentialsGroup(props: {
	value: EmailSenderDraft
	hasCredentials: boolean
	disabled: boolean
	onChange: EmailSenderFormProps['onChange']
}) {
	const { value, disabled, onChange } = props
	return (
		<fieldset className="flex flex-col gap-3 rounded-ds-md border border-ds-border p-3">
			<legend className="px-1 text-ds-sm text-ds-text-muted">Own SES credentials (optional)</legend>
			{props.hasCredentials ? (
				<Badge variant="success" size="sm">
					Credentials stored
				</Badge>
			) : null}
			<TextField
				label="Access key ID"
				value={value.accessKeyId}
				disabled={disabled}
				onChange={(v) => onChange('accessKeyId', v)}
			/>
			<TextField
				label="Secret access key"
				type="password"
				value={value.secretAccessKey}
				disabled={disabled}
				onChange={(v) => onChange('secretAccessKey', v)}
			/>
			<TextField label="Region" value={value.region} disabled={disabled} onChange={(v) => onChange('region', v)} />
		</fieldset>
	)
}

/**
 * The tenant's email sender (TO-382): provider, from name, from address,
 * reply-to, the unsubscribe mailbox (TO-138 — the `mailto:` of a marketing
 * email's `List-Unsubscribe`) and optional bring-your-own SES credentials.
 * Pure and controlled.
 */
export function EmailSenderForm({
	value,
	hasCredentials,
	onChange,
	onSave,
	onRemove,
	isDisabled = false,
	isBusy = false,
	error,
}: EmailSenderFormProps): ReactElement {
	const locked = isDisabled || isBusy
	return (
		<section aria-label="Sender" className="flex flex-col gap-4">
			<ProviderSelect value={value.provider} disabled={locked} onChange={(v) => onChange('provider', v)} />
			<TextField label="From name" value={value.fromName} disabled={locked} onChange={(v) => onChange('fromName', v)} />
			<AddressFields value={value} disabled={locked} onChange={onChange} />
			<CredentialsGroup value={value} hasCredentials={hasCredentials} disabled={locked} onChange={onChange} />
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<div className="flex gap-2">
				<Button isDisabled={locked} onPress={onSave}>
					Save sender
				</Button>
				<Button variant="secondary" isDisabled={locked} onPress={onRemove}>
					Remove
				</Button>
			</div>
		</section>
	)
}
